import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

// Define subscription plans
export type PlanType = 'free' | 'standard' | 'enterprise';

export interface Plan {
  id: PlanType;
  name: string;
  price: number;
  billingPeriod: 'year';
  features: string[];
  certificateLimit: number;
}

export const PLANS: Record<PlanType, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    price: 0,
    billingPeriod: 'year',
    features: [
      'Monitor up to 3 domains',
      'Email notifications',
      'Basic expiration alerts',
      'Community support',
    ],
    certificateLimit: 3,
  },
  standard: {
    id: 'standard',
    name: 'Standard',
    price: 99,
    billingPeriod: 'year',
    features: [
      'Monitor up to 15 domains',
      'Email & SMS notifications',
      'Advanced monitoring',
      'Priority support',
      '7-day early alerts',
    ],
    certificateLimit: 15,
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    price: 180,
    billingPeriod: 'year',
    features: [
      'Unlimited domains',
      'Email, SMS, Teams & Slack notifications',
      'Real-time monitoring',
      'Dedicated support',
      '30-day early alerts',
      'Custom expiration rules',
      // 'API access',
    ],
    certificateLimit: Infinity,
  },
};

// Define the shape of the subscription data as stored in Firestore
interface FirestoreSubscriptionData {
  plan?: PlanType; // For backward compatibility if older data exists
  plan_id?: PlanType;
  plan_status?: 'active' | 'pending_payment' | 'cancelled' | 'past_due' | string; // More comprehensive status
  certificate_limit?: number;
  last_payment_date?: string; // Firebase Timestamp, e.g., from Stripe webhook
  plan_activated_at?: string; // Firebase Timestamp
  // stripe_subscription_id?: string; // Example of other potential fields
}

// UserSubscription is what the context manages internally, derived from FirestoreSubscriptionData

interface SubscriptionContextType {
  currentPlan: Plan;
  certificateLimit: number;
  changePlan: (plan: PlanType) => Promise<void>;
  isSubscribing: boolean;
  subscribeToPlan: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  currentPlan: PLANS.free,
  certificateLimit: PLANS.free.certificateLimit,
  changePlan: async () => {},
  isSubscribing: false,
  subscribeToPlan: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentPlan, setCurrentPlan] = useState<Plan>(PLANS.free);
  const [certificateLimit, setCertificateLimit] = useState<number>(PLANS.free.certificateLimit);
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Load user's subscription on auth change
  useEffect(() => {
    const loadUserSubscription = async () => {
      if (!user) {
        setCurrentPlan(PLANS.free);
        setCertificateLimit(PLANS.free.certificateLimit);
        return;
      }

      try {
        const userRef = doc(db, 'users', user.id);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const subData = userData.subscription as FirestoreSubscriptionData | undefined;

          let finalPlanId: PlanType = 'free';
          let finalCertLimit: number = PLANS.free.certificateLimit;

          if (subData) {
            const planIdFromDb = subData.plan_id || subData.plan || 'free'; // Use plan_id, fallback to plan, then 'free'
            // Check if the plan from DB is valid and active
            if (subData.plan_status === 'active' && PLANS[planIdFromDb]) {
              finalPlanId = planIdFromDb;
              finalCertLimit = subData.certificate_limit ?? PLANS[finalPlanId].certificateLimit;
            } else {
              // If plan is not active, or plan_id is invalid, default to free plan
              finalPlanId = 'free';
              finalCertLimit = PLANS.free.certificateLimit; // Ensure limit matches the effective free plan
            }
          }
          
          setCurrentPlan(PLANS[finalPlanId]);
          setCertificateLimit(finalCertLimit);
        } else {
          // New user: Create user document with default free subscription and notifications
          const defaultSubscriptionData: FirestoreSubscriptionData = {
            plan_id: 'free',
            plan_status: 'active',
            certificate_limit: PLANS.free.certificateLimit,
          };
          
          await setDoc(userRef, {
            userId: user.id,
            email: user.email,
            name: user.name,
            createdAt: serverTimestamp(),
            subscription: defaultSubscriptionData,
            certificates: [],
            notifications: { // Initialize default notification preferences
                email: true,
                sms: false, 
                weeklySummary: false, 
                monthlySummary: false,
                slack_webhook: '',
                teams_webhook: '',
                custom_webhook: ''
            }
          }, { merge: true }); // Use merge if other parts of user doc might be set elsewhere initially
          
          setCurrentPlan(PLANS.free);
          setCertificateLimit(PLANS.free.certificateLimit);
        }
      } catch (error) {
        console.error('Error loading subscription:', error);
        setCurrentPlan(PLANS.free);
        setCertificateLimit(PLANS.free.certificateLimit);
      }
    };

    loadUserSubscription();
  }, [user]);

  // Change plan
  const changePlan = async (plan: PlanType) => {
    if (!user) throw new Error('User must be logged in');

    // Note: For paid plans, status should ideally be confirmed by a webhook after successful payment.
    // This client-side changePlan might set it to 'active' optimistically or 'pending_payment'.
    // For 'free' plan, it's always 'active'.
    const newPlanStatus = plan === 'free' ? 'active' : 'active'; // Assuming payment is confirmed if this is called for paid plan

    try {
      const userRef = doc(db, 'users', user.id);
      await updateDoc(userRef, {
        'plan_id': plan,
        'plan': plan, // maintain for potential backward compatibility or other reads
        'plan_status': newPlanStatus,
        'certificate_limit': PLANS[plan].certificateLimit,
        'plan_activated_at': serverTimestamp(),
        // 'last_payment_date': if changing to paid plan and payment just occurred.
      });
      
      setCurrentPlan(PLANS[plan]);
      setCertificateLimit(PLANS[plan].certificateLimit);
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error;
    }
  };


  // Subscribe to a plan (no-op, handled by Stripe/payment backend)
  const subscribeToPlan = async () => {
    setIsSubscribing(true);
    try {
      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <SubscriptionContext.Provider
      value={{
        currentPlan,
        certificateLimit,
        changePlan,
        isSubscribing,
        subscribeToPlan,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};